import { NavLink } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { 
  HomeIcon,
  MapIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  MapPinIcon,
  BellAlertIcon,
  ChartBarIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  ViewfinderCircleIcon,
  PencilSquareIcon,
  BookOpenIcon,
  TvIcon,
  Squares2X2Icon,
  FilmIcon,
  ChatBubbleLeftRightIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useRealtimeStore } from '../store/realtimeStore';
import { useAuthStore } from '../store/authStore';
import { useUnitStore } from '../store/unitStore';
import { useCommsStore } from '../store/commsStore';
import clsx from 'clsx';

export default function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { sosEvents, isConnected } = useRealtimeStore();
  const { user } = useAuthStore();
  const { units, selectedUnitId, setSelectedUnitId, fetchUnits, getSelectedUnit, isHQ: isHQFn } = useUnitStore();
  const unreadComms = useCommsStore((s) => s.unread);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const activeSOS = sosEvents.filter(e => 
    ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(e.status)
  ).length;

  // Check if user is Brigade HQ (86ISB counts as HQ)
  const isHQ = isHQFn(user?.unitId ?? null);

  const navigation = useMemo(() => {
    const items: Array<{ name: string; href: string; icon: any; critical?: boolean; badge?: number }> = [
      { name: 'Dashboard', href: '/', icon: HomeIcon },
      { name: 'Command Center', href: '/command-center', icon: Squares2X2Icon },
      { name: 'Live Map', href: '/map', icon: MapIcon },
      { name: 'Targets', href: '/targets', icon: ViewfinderCircleIcon },
      { name: 'Operations', href: '/operations', icon: ClipboardDocumentListIcon },
      { name: 'Op Plans', href: '/op-plans', icon: RectangleStackIcon },
      { name: 'Comms', href: '/comms', icon: ChatBubbleLeftRightIcon, badge: unreadComms },
    ];

    // Location Studio + Unit Analysis + Alert Rules are HQ (superuser) only.
    if (isHQ) {
      items.push({ name: 'Location Studio', href: '/location-studio', icon: PencilSquareIcon });
      items.push({ name: 'Unit Analysis', href: '/unit-statistics', icon: ChartBarIcon });
      items.push({ name: 'Alert Rules', href: '/alert-rules', icon: ShieldCheckIcon });
      items.push({ name: 'Admin', href: '/admin', icon: UsersIcon });
    }

    items.push(
      { name: 'War Diary', href: '/war-diary', icon: BookOpenIcon },
      { name: 'Ops Wall', href: '/ops-wall', icon: TvIcon },
      { name: 'Simulation', href: '/simulation', icon: FilmIcon },
      { name: 'Analytics', href: '/analytics', icon: ChartPieIcon },
      { name: 'SOS Center', href: '/sos', icon: ExclamationTriangleIcon, critical: true },
      { name: 'Alerts', href: '/alerts', icon: BellAlertIcon },
      { name: 'Geofences', href: '/geofences', icon: MapPinIcon },
      { name: 'Platoons', href: '/platoons', icon: UserGroupIcon },
      { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
    );

    return items;
  }, [isHQ, unreadComms]);

  // Fetch units on mount to get logo URLs (needed for all users)
  // Also fetch when user becomes available (after login)
  useEffect(() => {
    // Always fetch if units are empty, or if user is available and we need their unit
    if (units.length === 0 || (user?.unitId && !units.find(b => b.id === user.unitId))) {
      fetchUnits();
    }
  }, [user, units, fetchUnits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUnitDropdown(false);
      }
    };

    if (showUnitDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUnitDropdown]);

  const selectedUnit = getSelectedUnit();

  // Determine which unit's mark to show (reactive - updates when units load).
  // Priority: HQ's selected unit > user's own unit > brigade default.
  // Logo is driven by the unit's API-provided logoUrl with an aorColor monogram
  // fallback — no hardcoded per-code whitelist, so every unit (incl. SBW) renders.
  const brandUnit = useMemo(() => {
    if (isHQ) return selectedUnit ?? null;
    if (user?.unitId) return units.find(u => u.id === user.unitId) ?? null;
    return null;
  }, [user?.unitId, units, selectedUnit, isHQ]);

  const logoUrl = brandUnit?.logoUrl ?? null;
  const logoAlt = brandUnit ? `${brandUnit.name} Logo` : '86 Independent Signal Brigade';
  const monogram = (brandUnit?.code ?? '86ISB').slice(0, 4);
  const monogramColor = brandUnit?.aorColor ?? '#3399ff';
  // The brigade/HQ brand (and the pre-load default) shows the 86 Signal Bde crest;
  // battalions without their own API logoUrl keep the aorColor monogram identity.
  const isBrigadeBrand = !brandUnit || brandUnit.role === 'HQ' || brandUnit.code === '86ISB';
  const effectiveLogo = logoUrl ?? (isBrigadeBrand ? '/86isb-logo.png' : null);

  return (
    <div className="w-full h-full bg-command-panel border-r border-command-border flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-command-border">
        {effectiveLogo ? (
          <img src={effectiveLogo} alt={logoAlt} className="w-10 h-10 object-contain" />
        ) : (
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center text-[10px] font-bold text-white font-tactical"
            style={{ backgroundColor: monogramColor }}
            title={logoAlt}
            aria-label={logoAlt}
          >
            {monogram}
          </div>
        )}
        <div className="ml-3">
          <h1 className="text-lg font-display font-bold text-command-accent tracking-wider">
            OpMonitor
          </h1>
          <p className="text-xs text-gray-500 font-tactical">ST-2026 COMMAND CENTER</p>
        </div>
      </div>

      {/* Unit Selector (Brigade HQ only) */}
      {isHQ && (
        <div className="px-6 py-3 border-b border-command-border">
          <label className="block text-xs text-gray-500 mb-2 font-tactical">CURRENT VIEW</label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setShowUnitDropdown(!showUnitDropdown)}
              className={clsx(
                'w-full px-3 py-2 min-h-11 rounded-lg bg-command-bg border transition-colors touch-manipulation',
                'text-sm text-gray-100 flex items-center justify-between',
                'focus:outline-none focus:border-command-accent',
                'border-command-border hover:border-command-accent/50'
              )}
            >
              <span className="truncate">
                {selectedUnitId === null
                  ? 'Brigade HQ (All)'
                  : selectedUnit?.name || 'Select Unit'}
              </span>
              <ChevronDownIcon className={clsx(
                'w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2',
                showUnitDropdown && 'rotate-180'
              )} />
            </button>
            
            {showUnitDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-command-bg border border-command-border rounded-lg shadow-lg max-h-60 overflow-auto">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUnitId(null);
                    setShowUnitDropdown(false);
                    window.location.reload(); // Reload to apply filter
                  }}
                  className={clsx(
                    'w-full px-3 py-2 min-h-11 text-left text-sm text-gray-100 hover:bg-command-accent/20 transition-colors touch-manipulation',
                    selectedUnitId === null && 'bg-command-accent/30'
                  )}
                >
                  <div className="font-semibold">Brigade HQ</div>
                  <div className="text-xs text-gray-400">View all units</div>
                </button>
                {units
                  .filter(unit => unit.role !== 'HQ') // Exclude the HQ unit - it's represented by "Brigade HQ (All)"
                  .map((unit) => (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => {
                      setSelectedUnitId(unit.id);
                      setShowUnitDropdown(false);
                      window.location.reload(); // Reload to apply filter
                    }}
                    className={clsx(
                      'w-full px-3 py-2 min-h-11 text-left text-sm text-gray-100 hover:bg-command-accent/20 transition-colors border-t border-command-border flex items-center gap-2 touch-manipulation',
                      selectedUnitId === unit.id && 'bg-command-accent/30'
                    )}
                  >
                    {/* Unit color indicator */}
                    <div 
                      className="w-3 h-3 rounded-full border border-gray-600 flex-shrink-0"
                      style={{ backgroundColor: unit.aorColor }}
                      title={`${unit.name} color`}
                    />
                    <div className="flex-1">
                      <div className="font-semibold">{unit.name}</div>
                      <div className="text-xs text-gray-400">{unit.code}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className="px-6 py-3 border-b border-command-border">
        <div className="flex items-center gap-2">
          <div className={clsx(
            'w-2 h-2 rounded-full',
            isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
          )} />
          <span className="text-xs text-gray-400 font-tactical">
            {isConnected ? 'CONNECTED' : 'DISCONNECTED'}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-3">
          {navigation.map((item) => (
              <li key={item.name}>
              <NavLink
                to={item.href}
                onClick={onNavigate}
                className={({ isActive }) => clsx(
                  'flex items-center gap-3 px-3 py-2.5 min-h-11 rounded-lg transition-all duration-200 touch-manipulation',
                  'text-sm font-medium',
                  isActive
                    ? 'bg-command-accent/10 text-command-accent border border-command-accent/30'
                    : 'text-gray-400 hover:text-gray-100 hover:bg-command-border/50',
                  item.critical && activeSOS > 0 && 'animate-pulse'
                )}
              >
                <item.icon className={clsx(
                  'w-5 h-5',
                  item.critical && activeSOS > 0 && 'text-red-500'
                )} />
                <span>{item.name}</span>
                
                {/* SOS Badge */}
                {item.critical && activeSOS > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                    {activeSOS}
                  </span>
                )}

                {/* Unread badge (Comms) */}
                {!item.critical && (item.badge ?? 0) > 0 && (
                  <span className="ml-auto bg-command-accent text-command-bg text-xs font-bold px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </NavLink>
              </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-command-border space-y-3">
        {/* Logged in Commander Info */}
        {user && (
          <div className="mb-3 pb-3 border-b border-command-border">
            <p className="text-xs text-gray-500 font-tactical mb-1">LOGGED IN AS</p>
            <div className="flex items-center gap-2">
              {user.unitId && (
                <div 
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ 
                    backgroundColor: units.find(b => b.id === user.unitId)?.aorColor || '#6b7280' 
                  }}
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-200 truncate">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {user.unitId 
                    ? (units.find(b => b.id === user.unitId)?.name || 'Unit')
                    : 'Brigade HQ'
                  } • {user.role?.replace(/_/g, ' ').replace(/\s+User$/i, '')}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="text-center">
          <p className="text-xs text-gray-500 font-tactical">
            v1.0.0 • SECURE
          </p>
        </div>
      </div>
    </div>
  );
}

